import { useRef } from "react";

export type InputRef = HTMLInputElement | null;

export const useTimeTableNavigation = (rows: number, cols: number) => {
    // A 2D array to store references to all input elements (by [row][col])
    const inputRefs = useRef<InputRef[][]>([]);

    const setInputRef = (element: InputRef, row: number, col: number) => {
        // Initialize row if undefined
        if (!inputRefs.current[row])
            inputRefs.current[row] = [];

        // Assign input ref to specific row & col
        inputRefs.current[row][col] = element;
    };

    const isDisabled = (row: number, col: number): boolean => {
        const ref = inputRefs.current[row]?.[col];
        return !ref || ref.disabled;
    };

    const getNextPosition = (startRow: number, startCol: number): [number, number] => {
        let row = startRow;
        let col = startCol;

        do {
            // Move to the next row
            row += 1;

            // Skip row 2
            if (row === 2) row = 3;

            // Wrap to next column if row goes out of bounds
            if (row >= rows) {
                row = 0;
                col += 1;
            }

            // Wrap to start if column goes out of bounds
            if (col >= cols) {
                col = -1;
            }

            // Break if looped all the way back
            if (row === startRow && col === startCol) {
                break;
            }
        } while (isDisabled(row, col)); // Skip disabled inputs

        return [row, col];
    };

    const handleKeyDown = (e: React.KeyboardEvent, row: number, col: number) => {
        if (e.key !== "Tab") return;

        e.preventDefault();

        // Get next valid position and  next input ref
        const [nextRow, nextCol] = getNextPosition(row, col);
        const nextRef = inputRefs.current[nextRow]?.[nextCol];

        // Focus the next input if it's valid and enabled
        if (nextRef && !nextRef.disabled) {
            nextRef.focus();
        }
    };

    return {
        setInputRef,
        handleKeyDown,
    };
};

